// ═══════════════════════════════════════════════════════════════
// templateXlsx.ts – a 2026-os közzétételi lista xlsx letöltése és visszatöltése (sablon-szerkesztő)
//
// Formátum: „Útmutató” munkalap + fejezetenként egy munkalap („1. …”, „2. …”, „3. …”), oszlopok:
//   Ssz. | Adat | Frissítés | Megőrzés | Rövid név | Leírás | Külső linkek
// A főpontok sorai (Ssz. = „2.2”) nem vehetők fel és nem törölhetők; az al-tételek sorai
// (Ssz. = „2.4 › Adóigazgatás”) szabadon felvehetők, törölhetők. A mentés előtt a szerver
// is ellenőriz, ez a modul csak beolvas és egyeztet.
// ═══════════════════════════════════════════════════════════════
import type { TemplateChapter, TemplateRecord } from '../api/client';

export const XLSX_COLUMNS = ['Ssz.', 'Adat', 'Frissítés', 'Megőrzés', 'Rövid név', 'Leírás', 'Külső linkek'] as const;
const KEYS: Record<string, keyof TemplateRecord | 'pont'> = {
  'ssz.': 'pont', 'ssz': 'pont', 'adat': 'cim', 'frissítés': 'frissites', 'megőrzés': 'megorzesi_ido',
  'rövid név': 'short', 'adat_rovid': 'short', 'leírás': 'leiras', 'külső linkek': 'kulso_linkek',
};

export const normPont = (p: unknown) => String(p ?? '').trim().replace(/\s*›\s*/g, ' › ').replace(/\.+$/, '');
export const isSub = (p: unknown) => String(p ?? '').includes('›');

const sheetName = (ch: TemplateChapter, i: number) =>
  `${i + 1}. ${ch.pageTitle.replace(/^[0-9]+\.\s*/, '')}`.replace(/[\][:*?/\\]/g, ' ').slice(0, 31).trim();

export async function downloadTemplateXlsx(chapters: TemplateChapter[], fileName = 'kozzeteteli_lista_2026.xlsx') {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const guide = [
    ['Közzétételi lista 2026 – Infotv. 1. melléklet (szerkeszthető sablon)'],
    [''],
    ['Fejezetenként egy munkalap. Oszlopok: ' + XLSX_COLUMNS.join(' | ')],
    ['Főpont (Ssz. pl. „2.2”): a sor nem törölhető és új főpont nem vehető fel; az Adat (a pont címe), a Frissítés, a Megőrzés, a Rövid név (mappanév), a Leírás és a Külső linkek írható át.'],
    ['Al-tétel (Ssz. pl. „2.4 › Adóigazgatás”): a csoport a › jel után; sor felvehető, törölhető, átírható. Frissítés, Megőrzés, Rövid név itt nem használt.'],
    ['Külső linkek: soronként „Címke — URL”. A {{TOKEN}} és {{BLOKK_…}} helyek a szervezet adataival töltődnek ki.'],
    ['Visszatöltés: Szervezetek → Sablon szerkesztése → XLSX visszatöltése. Mentés előtt a változások ellenőrizhetők.'],
  ];
  const gws = XLSX.utils.aoa_to_sheet(guide);
  gws['!cols'] = [{ wch: 140 }];
  XLSX.utils.book_append_sheet(wb, gws, 'Útmutató');
  chapters.forEach((ch, i) => {
    const rows: (string)[][] = [[...XLSX_COLUMNS]];
    for (const r of ch.records) {
      rows.push([
        String(r.pont ?? ''), String(r.cim ?? ''),
        isSub(r.pont) ? '' : String(r.frissites ?? ''),
        isSub(r.pont) ? '' : String(r.megorzesi_ido ?? ''),
        isSub(r.pont) ? '' : String(r.short ?? ''),
        String(r.leiras ?? ''), String(r.kulso_linkek ?? ''),
      ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 26 }, { wch: 70 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 80 }, { wch: 60 }];
    ws['!autofilter'] = { ref: `A1:G${rows.length}` };
    XLSX.utils.book_append_sheet(wb, ws, sheetName(ch, i));
  });
  XLSX.writeFile(wb, fileName);
}

export interface XlsxImportResult {
  chapters: TemplateChapter[];
  errors: string[];
  warnings: string[];
}

/** Az xlsx beolvasása a jelenlegi lista alapján (a fejezetek és a főpontok szerkezete onnan jön). */
export async function parseTemplateXlsx(file: File, base: TemplateChapter[]): Promise<XlsxImportResult> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const errors: string[] = [];
  const warnings: string[] = [];
  const chapters: TemplateChapter[] = base.map((ch, ci) => {
    const name = wb.SheetNames.find((n) => n.trim().startsWith(`${ci + 1}.`));
    if (!name) { errors.push(`Hiányzik a(z) „${ci + 1}. …” munkalap (${ch.pageTitle}).`); return ch; }
    const rows = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, defval: '', raw: false, blankrows: false });
    const head = (rows[0] || []).map((h) => String(h ?? '').trim().toLowerCase());
    const col = new Map<string, number>();
    head.forEach((h, i) => { const k = KEYS[h]; if (k && !col.has(k)) col.set(k, i); });
    if (!col.has('pont') || !col.has('cim')) { errors.push(`„${name}”: az első sorban nincs „Ssz.” és „Adat” oszlop.`); return ch; }
    const get = (row: unknown[], k: string) => (col.has(k) ? String(row[col.get(k)!] ?? '') : undefined);

    const baseMain = new Map(ch.records.filter((r) => !isSub(r.pont)).map((r) => [normPont(r.pont), r]));
    const seenMain = new Set<string>();
    const records: TemplateRecord[] = [];
    rows.slice(1).forEach((row, i) => {
      const pont = normPont(get(row, 'pont'));
      if (!pont) return;
      const line = `„${name}” ${i + 2}. sor (${pont})`;
      if (isSub(pont)) {
        const main = pont.split(' › ')[0];
        if (!baseMain.has(main)) { errors.push(`${line}: nincs ilyen főpont ebben a fejezetben.`); return; }
        records.push({ pont, cim: (get(row, 'cim') || '').trim(), leiras: get(row, 'leiras') ?? '', kulso_linkek: get(row, 'kulso_linkek') ?? '' });
        return;
      }
      const b = baseMain.get(pont);
      if (!b) { errors.push(`${line}: új főpont nem vehető fel (csak al-tétel, pl. „2.4 › Csoport”).`); return; }
      if (seenMain.has(pont)) { errors.push(`${line}: a főpont kétszer szerepel.`); return; }
      seenMain.add(pont);
      const pick = (k: keyof TemplateRecord) => { const v = get(row, k as string); return v === undefined ? (b[k] as string | undefined) : v; };
      records.push({ ...b, cim: (pick('cim') || '').trim(), frissites: pick('frissites'), megorzesi_ido: pick('megorzesi_ido'), short: pick('short'), leiras: pick('leiras'), kulso_linkek: pick('kulso_linkek') });
    });
    for (const p of baseMain.keys()) if (!seenMain.has(p)) errors.push(`„${name}”: hiányzik a(z) ${p} főpont sora (főpont nem törölhető).`);
    // a főpontok sorrendje rögzített: az al-tételek a saját főpontjuk után kerülnek, az xlsx-beli sorrendben
    const ordered: TemplateRecord[] = [];
    for (const m of ch.records.filter((r) => !isSub(r.pont))) {
      const mr = records.find((r) => !isSub(r.pont) && normPont(r.pont) === normPont(m.pont));
      if (mr) ordered.push(mr);
      for (const s of records) if (isSub(s.pont) && s.pont.split(' › ')[0] === normPont(m.pont)) ordered.push(s);
    }
    const sheetOrder = records.filter((r) => !isSub(r.pont)).map((r) => normPont(r.pont)).join(',');
    if (sheetOrder !== [...baseMain.keys()].filter((p) => seenMain.has(p)).join(',')) warnings.push(`„${name}”: a főpontok sorrendje az xlsx-ben eltér – a lista sorrendje marad.`);
    return { ...ch, records: ordered };
  });
  return { chapters, errors, warnings };
}

const FIELDS: (keyof TemplateRecord)[] = ['cim', 'short', 'frissites', 'megorzesi_ido', 'leiras', 'kulso_linkek'];
export const FIELD_LABELS: Record<string, string> = { cim: 'cím', short: 'rövid név', frissites: 'frissítés', megorzesi_ido: 'megőrzés', leiras: 'leírás', kulso_linkek: 'külső linkek' };
export const recKey = (r: TemplateRecord) => (isSub(r.pont) ? `${normPont(r.pont)}|${String(r.cim).trim()}` : normPont(r.pont));

export interface TemplateDiff { changed: { pont: string; fields: string[] }[]; added: string[]; removed: string[]; total: number }

export function diffTemplates(a: TemplateChapter[], b: TemplateChapter[]): TemplateDiff {
  const idx = (chs: TemplateChapter[]) => { const m = new Map<string, TemplateRecord>(); for (const ch of chs) for (const r of ch.records) m.set(recKey(r), r); return m; };
  const A = idx(a); const B = idx(b);
  const changed: TemplateDiff['changed'] = []; const added: string[] = []; const removed: string[] = [];
  for (const [k, rb] of B) {
    const ra = A.get(k);
    if (!ra) { added.push(k.replace('|', ' – ')); continue; }
    const f = FIELDS.filter((x) => String(ra[x] ?? '').trim() !== String(rb[x] ?? '').trim());
    if (f.length) changed.push({ pont: isSub(rb.pont) ? `${normPont(rb.pont)} – ${rb.cim}` : normPont(rb.pont), fields: f.map((x) => FIELD_LABELS[x as string] || String(x)) });
  }
  for (const k of A.keys()) if (!B.has(k)) removed.push(k.replace('|', ' – '));
  return { changed, added, removed, total: changed.length + added.length + removed.length };
}
